import { useEffect, useState } from 'react';
import { Mail, Pencil, UserCheck, UserX } from 'lucide-react';
import api from '../../../utils/api';
import { apiErrorMessage } from '../../../utils/apiError';
import { PartnerEndpoints } from '../../../utils/endpoints';
import { Constants } from '../../../utils/constants';
import { Permissions, roleLabel } from '../../../utils/permissions';
import { formatLagos } from '../../../utils/datetime';
import { formatCount } from '../../../utils/money';
import { useAuth } from '../../../AuthContext';
import { useServerTable } from '../../../hooks/useServerTable';
import PageHeader from '../../shell/PageHeader';
import {
  Alert,
  Button,
  DataTable,
  DateRangeFilter,
  FilterSelect,
  IconButton,
  Pagination,
  SearchInput,
  StatusPill,
  TableCard,
} from '../../ui';
import InviteMemberModal from '../modals/InviteMemberModal';
import EditMemberModal from '../modals/EditMemberModal';
import ConfirmActionModal from '../modals/ConfirmActionModal';

const Team = () => {
  const { userId, hasPermission } = useAuth();
  const canCreate = hasPermission(Permissions.teamCreate);
  const canUpdate = hasPermission(Permissions.teamUpdate);

  const table = useServerTable(PartnerEndpoints.team, {
    sort: 'first_name',
    fallbackError: 'Could not load team members.',
  });

  const [roles, setRoles] = useState([]);
  const [actionError, setActionError] = useState('');
  const [success, setSuccess] = useState('');

  const [inviteOpen, setInviteOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [deactivateTarget, setDeactivateTarget] = useState(null);
  const [resendTarget, setResendTarget] = useState(null);

  useEffect(() => {
    if (!success) return undefined;
    const timer = setTimeout(() => setSuccess(''), 5000);
    return () => clearTimeout(timer);
  }, [success]);

  useEffect(() => {
    if (!canCreate && !canUpdate) return;

    const fetchRoles = async () => {
      try {
        const { data } = await api.get(PartnerEndpoints.teamRoles);
        setRoles(data?.data || []);
      } catch {
        setRoles([]);
      }
    };

    fetchRoles();
  }, [canCreate, canUpdate]);

  const isSelf = (member) => Boolean(userId) && member.id === userId;
  const isGuarded = (member) => Boolean(member.is_owner) || isSelf(member);

  const guardReason = (member) =>
    member.is_owner
      ? 'The account owner is managed by CashAfrica support.'
      : 'You cannot change your own role or access.';

  const handleInvite = async (payload) => {
    await api.post(PartnerEndpoints.team, payload);
    setSuccess(`An invitation email has been sent to ${payload.email}.`);
    table.setPage(0);
    table.refetch();
  };

  const handleEdit = async (payload) => {
    await api.put(PartnerEndpoints.teamMember(editTarget.id), payload);
    setSuccess('Member updated.');
    table.refetch();
  };

  const changeStatus = async (member, nextStatus) => {
    await api.patch(PartnerEndpoints.teamMemberStatus(member.id), { status: nextStatus });
    setSuccess(
      nextStatus === 'deactivated'
        ? `${member.full_name}'s access has been revoked.`
        : `${member.full_name} has been reactivated.`
    );
    table.refetch();
  };

  const handleActivate = async (member) => {
    setActionError('');
    try {
      await changeStatus(member, 'active');
    } catch (err) {
      setActionError(apiErrorMessage(err, 'Could not activate this member.'));
    }
  };

  const handleResendInvite = async () => {
    const { data } = await api.post(PartnerEndpoints.resendTeamInvite(resendTarget.id), {});
    setSuccess(data?.message || 'A new password has been emailed.');
  };

  const columns = [
    {
      key: 'full_name',
      header: 'Name',
      sortKey: 'first_name',
      render: (member) => (
        <span className="whitespace-nowrap">
          {member.full_name}
          {member.is_owner && <span className="badge bg-primary-500/10 text-primary-600 ml-2">Owner</span>}
          {isSelf(member) && <span className="badge bg-secondary-500/10 text-secondary-700 ml-2">You</span>}
        </span>
      ),
    },
    { key: 'email', header: 'Email' },
    { key: 'phone_number', header: 'Phone' },
    {
      key: 'role',
      header: 'Role',
      render: (member) => (
        <span className="badge bg-secondary-500/10 text-secondary-700">
          {member.role?.label || roleLabel(member.role?.name)}
        </span>
      ),
    },
    {
      key: 'status',
      filter: (
        <FilterSelect
          value={table.filters.status ?? ''}
          onChange={(value) => table.setFilter('status', value)}
          options={Constants.teamMemberStatuses}
          label="All"
          ariaLabel="Filter by status"
        />
      ),
      header: 'Status',
      align: 'center',
      render: (member) => <StatusPill status={member.status} />,
    },
    {
      key: 'created_at',
      sortKey: 'created_at',
      sortDefault: 'desc',
      filter: (
        <DateRangeFilter
          label="Added"
          from={table.from}
          to={table.to}
          onFromChange={table.setFrom}
          onToChange={table.setTo}
        />
      ),
      header: 'Added',
      render: (member) => <span className="whitespace-nowrap">{formatLagos(member.created_at)}</span>,
    },
  ];

  if (canUpdate) {
    columns.push({
      key: 'actions',
      header: 'Actions',
      align: 'center',
      render: (member) => {
        const guarded = isGuarded(member);
        const reason = guarded ? guardReason(member) : undefined;

        return (
          <div className="flex items-center justify-center gap-1">
            <IconButton
              icon={Pencil}
              label="Edit"
              tooltip="Edit member"
              variant="link-primary"
              onClick={() => setEditTarget(member)}
            />

            {member.status === 'deactivated' ? (
              <IconButton
                icon={UserCheck}
                label="Activate"
                tooltip={reason || 'Restore this member\u2019s access'}
                variant="link-success"
                disabled={guarded}
                onClick={() => handleActivate(member)}
              />
            ) : (
              <IconButton
                icon={UserX}
                label="Deactivate"
                tooltip={reason || 'Revoke this member\u2019s access'}
                variant="link-danger"
                disabled={guarded}
                onClick={() => setDeactivateTarget(member)}
              />
            )}

            <IconButton
              icon={Mail}
              label="Resend invite"
              tooltip="Email a new password and sign them out"
              onClick={() => setResendTarget(member)}
            />
          </div>
        );
      },
    });
  }

  return (
    <>
      <PageHeader
        title="Team"
        breadcrumbs={[{ label: 'Home', to: '/layout/dashboard' }, { label: 'Team' }]}
        actions={
          canCreate && (
            <Button variant="primary" onClick={() => setInviteOpen(true)}>
              Invite a member
            </Button>
          )
        }
      />

      <TableCard
        title="Team members"
        summary={{ label: 'Total', value: formatCount(table.total), loading: table.loading }}
        toolbar={
          <>
            <SearchInput
              value={table.search}
              onChange={table.setSearch}
              placeholder="Search name or email"
            />
          </>
        }
        footer={
          <div className="flex flex-col gap-3">
            <Pagination
              page={table.page}
              pageCount={table.pageCount}
              pageSize={table.pageSize}
              onPageChange={table.setPage}
              onPageSizeChange={table.setPageSize}
            />
            <p className="text-sm text-theme-secondarytextcolor dark:text-themedark-secondarytextcolor">
              Members cannot be removed here. Deactivate them to revoke access, or raise a support request to delete
              the account.
            </p>
          </div>
        }
      >
        {(success || actionError) && (
          <div className="flex flex-col gap-3 p-4 pb-0">
            {success && (
              <Alert variant="success" onDismiss={() => setSuccess('')}>
                {success}
              </Alert>
            )}
            {actionError && (
              <Alert variant="danger" onDismiss={() => setActionError('')}>
                {actionError}
              </Alert>
            )}
          </div>
        )}

        <DataTable
          columns={columns}
          rows={table.rows}
          rowKey={(member) => member.id}
          serial
          serialStart={table.page * table.pageSize}
          loading={table.loading}
          error={table.error}
          invalidDateRange={table.invalidDateRange}
          sort={table.sort}
          onSortChange={table.setSort}
          empty={{
            title: 'No team members',
            message: 'No one matches these filters yet. Invite a member to get started.',
          }}
        />
      </TableCard>

      <InviteMemberModal
        isOpen={inviteOpen}
        roles={roles}
        onClose={() => setInviteOpen(false)}
        onSubmit={handleInvite}
      />

      <EditMemberModal
        isOpen={Boolean(editTarget)}
        member={editTarget}
        roles={roles}
        canEditRole={Boolean(editTarget) && !isGuarded(editTarget)}
        roleLockReason={editTarget ? guardReason(editTarget) : ''}
        onClose={() => setEditTarget(null)}
        onSubmit={handleEdit}
      />

      <ConfirmActionModal
        isOpen={Boolean(deactivateTarget)}
        title="Deactivate member"
        message={
          deactivateTarget
            ? `This immediately revokes all of ${deactivateTarget.full_name}'s sessions and blocks them from signing in. It is reversible.`
            : ''
        }
        confirmLabel="Deactivate"
        confirmVariant="danger"
        onClose={() => setDeactivateTarget(null)}
        onConfirm={() => changeStatus(deactivateTarget, 'deactivated')}
      />

      <ConfirmActionModal
        isOpen={Boolean(resendTarget)}
        title="Resend invite and sign them out"
        message={
          !resendTarget
            ? ''
            : isSelf(resendTarget)
            ? `This is the only way to recover a password. It emails a new one to ${resendTarget.email}, and your current password stops working and you are signed out immediately.`
            : `This is the only way to recover a password. It emails a new one to ${resendTarget.email}, ends their current session and stops their existing password from working.`
        }
        confirmLabel="Resend and sign out"
        onClose={() => setResendTarget(null)}
        onConfirm={handleResendInvite}
      />
    </>
  );
};

export default Team;
